import type { RadioControl, RadioStationInfo } from '../audioService';
import { drum, epiano, fingerBass, flute, guitar, guitarMute, hammond, hornStab, melodica, piano, pickBass, softBass, strings, uprightBass, vibes, type DrumKind } from '../instruments';
import type { Mixer } from '../mixer';
import { bellPartials, noiseBurst, ringTick, softClipCurve, tone } from '../synth';
import { Programme, type Item } from './programme';
import { songBar, stepSeconds, swingOffset, type NoteEvent, type Song } from './songs';
import { STATIONS, type StationDef } from './stations';
import { SLOT_LABEL, type NewsSlot } from './newsDesk';
import type { NoteCue, SfxCue, Syllable } from './talk';

/**
 * In-car radio. Five stations, each an endless deterministic programme (`Programme`): records
 * (`songs.ts`), DJ links, adverts, jingles, the news. A look-ahead scheduler (setInterval ~25 ms,
 * ~150 ms ahead on the AudioContext clock) renders whatever falls in the window with the synthesised
 * instruments, the formant voices and the small SFX set. Every station runs its own clock from the
 * moment it is first tuned (starting part-way into its first record), so tuning in or back lands
 * mid-record.
 *
 * Receiver chain: station sum (music, duckable under the DJ + voices) -> highpass 200 -> lowpass at
 * the station's bandwidth (medium-wave pirates narrower) -> presence bump -> tape wow/flutter
 * (modulated short delay) -> valve-ish soft saturation (oversampled) -> gate (open while driving) ->
 * music bus. A little pink hiss per station sits in the band.
 */

const TICK_MS = 25;
const LOOKAHEAD = 0.15;
/** Seconds the sequencer keeps running after the gate closes before it stops to save CPU. */
const IDLE_STOP = 1.2;
/** Music level under a talking DJ (linear). */
const DJ_DUCK = 0.42;
/** Level of the records relative to the voices. */
const MUSIC_LEVEL = 0.62;
const VOICE_LEVEL = 0.68;

let satCurve: Float32Array<ArrayBuffer> | null = null;

interface FormantVoice {
  osc: OscillatorNode;
  bp: [BiquadFilterNode, BiquadFilterNode, BiquadFilterNode];
  env: GainNode;
  nodes: AudioNode[];
}

interface Chain {
  input: GainNode;
  lp: BiquadFilterNode;
  hiss: GainNode;
  gate: GainNode;
  nodes: AudioNode[];
}

export class Radio implements RadioControl {
  readonly stations: readonly RadioStationInfo[] = STATIONS.map((s) => ({ id: s.id, name: s.name, tagline: s.tagline }));
  current = -1;

  private readonly programmes = STATIONS.map((s) => new Programme(s.id));
  /** Audio time at which each station's clock started (NaN until first tuned). */
  private readonly origin: number[] = STATIONS.map(() => NaN);
  private chain: Chain | null = null;
  private sum: GainNode | null = null;
  /** Music-only input of the station sum (ducked under the DJ). */
  private musicIn: GainNode | null = null;
  private voiceIn: GainNode | null = null;
  private studio: FormantVoice | null = null;
  private phone: FormantVoice | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly timeouts = new Set<ReturnType<typeof setTimeout>>();
  private driving = false;
  private idleFor = 0;
  private running = false;
  /** Station time scheduled up to. */
  private cursor = 0;
  private barCache: { song: Song; bar: number; buckets: NoteEvent[][] } | null = null;
  /** Talk items whose speech duck has been scheduled. */
  private readonly ducked = new Set<number>();

  constructor(
    private readonly mx: Mixer,
    private readonly onChange: (index: number) => void,
  ) {}

  /**
   * The news desk (newsDesk.ts): asked once as a news, travel or DJ-link item comes on air whether it has a
   * bulletin for it. The running order is untouched; only what the ticker says changes.
   */
  bulletins: ((slot: NewsSlot) => string | null) | null = null;
  /** The item last seen on air (station:index) and the bulletin it carries. */
  private onAirKey = '';
  private onAirBulletin: string | null = null;

  get nowPlaying(): string {
    if (this.current < 0) return '';
    const def = STATIONS[this.current] as StationDef;
    const t = this.stationTime(this.current);
    const item = (this.programmes[this.current] as Programme).onAir(Math.max(0, t));
    const key = `${this.current}:${item.index}`;
    // Only while somebody is listening (in a car): a snapshot read on foot mustn't use the news up.
    if (key !== this.onAirKey && this.driving) {
      this.onAirKey = key;
      const slot: NewsSlot | null = item.kind === 'news' || item.kind === 'traffic' || item.kind === 'link' ? item.kind : null;
      const text = slot && this.bulletins ? this.bulletins(slot) : null;
      this.onAirBulletin = slot && text ? `${SLOT_LABEL[slot]}: ${text}` : null;
    }
    return this.onAirBulletin && key === this.onAirKey ? `${def.name} — ${this.onAirBulletin}` : `${def.name} — ${item.title}`;
  }

  /** The bulletin on air now ("News: …"), or null. Read `nowPlaying` first (it moves the item on). */
  get bulletin(): string | null {
    return this.current < 0 ? null : this.onAirBulletin;
  }

  /** Station time (s) of station `i` now. */
  stationTime(i: number): number {
    const o = this.origin[i] as number;
    // Not started yet (no sound, or a moment before the sequencer runs): where it will start.
    return Number.isNaN(o) ? (this.programmes[i] as Programme).tuneInOffset() : this.mx.now() - o;
  }

  tune(index: number): void {
    const n = this.stations.length;
    const i = Number.isFinite(index) ? Math.trunc(index) : -1;
    const next = i < 0 || i >= n ? -1 : i;
    if (next === this.current) return;
    this.stopSequencer();
    this.current = next;
    this.blip(next);
    this.resetStationBus();
    if (next >= 0 && this.driving) this.startSequencer(0.32);
    this.onChange(next);
  }

  next(): void {
    this.tune(this.current + 1 >= this.stations.length ? -1 : this.current + 1);
  }

  /** Per-frame: open/close the gate and start/stop the sequencer. */
  update(dt: number, driving: boolean): void {
    const mx = this.mx;
    this.driving = driving;
    if (!mx.ready || !mx.running) return;
    this.ensureChain();
    const now = mx.now();
    const on = driving && this.current >= 0;
    // In: quick (the radio is there as the door shuts). Out: a short tail (~0.8 s) as you step away,
    // not a hard cut the moment the door opens.
    this.chain?.gate.gain.setTargetAtTime(on ? 1 : 0, now, on ? 0.12 : 0.25);
    if (on) {
      this.idleFor = 0;
      if (!this.running) this.startSequencer(0.1);
    } else if (this.running) {
      this.idleFor += dt;
      if (this.idleFor > IDLE_STOP) this.stopSequencer();
    }
  }

  private ensureChain(): void {
    if (this.chain) return;
    const mx = this.mx;
    const ctx = mx.ctx as AudioContext;
    const nodes: AudioNode[] = [];
    const reg = <T extends AudioNode>(n: T): T => {
      nodes.push(n);
      return n;
    };
    const input = reg(ctx.createGain());
    input.gain.value = 0.9;
    const hp = reg(ctx.createBiquadFilter());
    hp.type = 'highpass';
    hp.frequency.value = 200;
    hp.Q.value = 0.7;
    const lp = reg(ctx.createBiquadFilter());
    lp.type = 'lowpass';
    lp.frequency.value = 5000;
    lp.Q.value = 0.8;
    const presence = reg(ctx.createBiquadFilter());
    presence.type = 'peaking';
    presence.frequency.value = 1800;
    presence.Q.value = 0.9;
    presence.gain.value = 2.5;
    // Tape wow and flutter: a short delay whose time wanders slowly and trembles quickly.
    const tape = reg(ctx.createDelay(0.05));
    tape.delayTime.value = 0.012;
    const wow = reg(ctx.createOscillator());
    wow.frequency.value = 0.45;
    const wowDepth = reg(ctx.createGain());
    wowDepth.gain.value = 0.00035;
    const flutter = reg(ctx.createOscillator());
    flutter.frequency.value = 6.3;
    const flutterDepth = reg(ctx.createGain());
    flutterDepth.gain.value = 0.00006;
    wow.connect(wowDepth).connect(tape.delayTime);
    flutter.connect(flutterDepth).connect(tape.delayTime);
    wow.start();
    flutter.start();
    // Valve warmth: a gentle tanh with a touch of even-order asymmetry from a DC offset.
    const shaper = reg(ctx.createWaveShaper());
    satCurve ??= softClipCurve(1.25);
    shaper.curve = satCurve;
    shaper.oversample = '2x';
    const gate = reg(ctx.createGain());
    gate.gain.value = 0;
    // Hiss: pink noise in the receiver band.
    const hissSrc = reg(mx.noiseSource('pink'));
    const hissHp = reg(ctx.createBiquadFilter());
    hissHp.type = 'highpass';
    hissHp.frequency.value = 1200;
    const hiss = reg(ctx.createGain());
    hiss.gain.value = 0;
    hissSrc.connect(hissHp).connect(hiss).connect(lp);
    mx.startNoise(hissSrc, ctx.currentTime);
    input.connect(hp).connect(lp).connect(presence).connect(tape).connect(shaper).connect(gate).connect(mx.buses.music);
    this.chain = { input, lp, hiss, gate, nodes };
    this.resetStationBus();
  }

  /** Fresh per-tuning sum bus; the old one fades out quickly so scheduled tails don't bleed through. */
  private resetStationBus(): void {
    const ctx = this.mx.ctx;
    const chain = this.chain;
    if (!ctx || !chain) return;
    const old = this.sum;
    const oldMusic = this.musicIn;
    const oldVoice = this.voiceIn;
    if (old) {
      old.gain.cancelScheduledValues(ctx.currentTime);
      old.gain.setTargetAtTime(0, ctx.currentTime, 0.03);
      const t = setTimeout(() => {
        this.timeouts.delete(t);
        old.disconnect();
        oldMusic?.disconnect();
        oldVoice?.disconnect();
      }, 400);
      this.timeouts.add(t);
    }
    this.dropVoices();
    const def = this.current >= 0 ? (STATIONS[this.current] as StationDef) : null;
    const sum = ctx.createGain();
    sum.gain.value = def ? def.trim : 1;
    sum.connect(chain.input);
    this.sum = sum;
    const musicIn = ctx.createGain();
    musicIn.gain.value = MUSIC_LEVEL;
    musicIn.connect(sum);
    this.musicIn = musicIn;
    const voiceIn = ctx.createGain();
    voiceIn.gain.value = VOICE_LEVEL;
    voiceIn.connect(sum);
    this.voiceIn = voiceIn;
    chain.lp.frequency.setTargetAtTime(def ? def.bandwidth : 5000, ctx.currentTime, 0.05);
    chain.hiss.gain.setTargetAtTime(def ? def.hiss : 0, ctx.currentTime, 0.1);
    this.ducked.clear();
    this.barCache = null;
  }

  private startSequencer(delay: number): void {
    const mx = this.mx;
    if (this.current < 0 || !mx.ctx || !mx.running || !this.sum) return;
    this.running = true;
    this.idleFor = 0;
    const now = mx.now();
    // First tune of this station: its clock starts part-way into the first record (`tuneInOffset`).
    if (Number.isNaN(this.origin[this.current] as number)) this.origin[this.current] = now + delay - (this.programmes[this.current] as Programme).tuneInOffset();
    const st = Math.max(0, now + delay - (this.origin[this.current] as number));
    this.cursor = st;
    this.barCache = null;
    if (!this.timer) this.timer = setInterval(() => this.safeTick(), TICK_MS);
  }

  private stopSequencer(): void {
    this.running = false;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const ctx = this.mx.ctx;
    if (ctx) {
      const t = ctx.currentTime;
      for (const v of [this.studio, this.phone]) {
        if (!v) continue;
        v.env.gain.cancelScheduledValues(t);
        v.env.gain.setValueAtTime(0.0001, t);
      }
    }
  }

  private safeTick(): void {
    try {
      this.tick();
    } catch (e) {
      if (typeof console !== 'undefined') console.warn('[audio] radio tick failed', e);
    }
  }

  private tick(): void {
    const mx = this.mx;
    if (!this.running || !mx.ctx || !this.sum || !mx.running) return;
    this.scheduleUntil(mx.now() + LOOKAHEAD);
  }

  /**
   * Schedule everything on the current station between the cursor and `horizon` (audio time). Public
   * so the offline audio lab can drive it without timers.
   */
  scheduleUntil(horizon: number): void {
    if (this.current < 0 || !this.sum) return;
    const origin = this.origin[this.current] as number;
    if (Number.isNaN(origin)) return;
    const prog = this.programmes[this.current] as Programme;
    const now = this.mx.now();
    // Resumed after a stall: skip what was missed.
    if (this.cursor < now - origin - 0.1) this.cursor = now - origin + 0.05;
    const from = this.cursor;
    const to = horizon - origin;
    if (to <= from) return;
    for (const item of prog.itemsIn(from, to)) {
      if (item.kind === 'song') this.scheduleSong(prog, item, from, to, origin);
      else this.scheduleTalk(item, from, to, origin);
    }
    this.cursor = to;
    if (Math.floor(to / 30) !== Math.floor(from / 30)) prog.forget(to);
  }

  // ---- songs ----------------------------------------------------------------------------------

  private scheduleSong(prog: Programme, item: Item, from: number, to: number, origin: number): void {
    const song = prog.songOf(item);
    const stepDur = stepSeconds(song.bpm);
    const total = song.bars * 16;
    let k = Math.max(0, Math.ceil((from - item.start) / stepDur - 1e-6));
    for (; k < total; k++) {
      const st = item.start + k * stepDur;
      if (st >= to) break;
      if (st < from) continue;
      this.scheduleStep(song, k, origin + st, stepDur);
    }
  }

  private scheduleStep(song: Song, step: number, time: number, stepDur: number): void {
    const bar = Math.floor(step / 16);
    const s = step % 16;
    let cache = this.barCache;
    if (!cache || cache.song !== song || cache.bar !== bar) {
      const buckets: NoteEvent[][] = Array.from({ length: 16 }, () => []);
      for (const e of songBar(song, bar)) (buckets[e.step] as NoteEvent[]).push(e);
      cache = this.barCache = { song, bar, buckets };
    }
    for (const e of cache.buckets[s] as NoteEvent[]) this.playEvent(song, e, time + swingOffset(e.step, song.swing) * stepDur, stepDur);
  }

  private playEvent(song: Song, e: NoteEvent, t: number, stepDur: number): void {
    const mx = this.mx;
    const d = (this.musicIn ?? this.sum) as AudioNode;
    const dur = Math.max(0.05, e.dur * stepDur);
    const v = e.vel;
    switch (e.inst) {
      case 'rhodes':
        epiano(mx, d, t, e.midi, dur, v, true);
        break;
      case 'hammond': {
        const reg = e.dur <= 1 && e.vel < 0.5 ? 'bubble' : e.dur >= 8 && e.vel < 0.45 ? 'pad' : 'comp';
        hammond(mx, d, t, e.midi, dur, v, true, reg);
        break;
      }
      case 'hammondLead':
        hammond(mx, d, t, e.midi, dur, v, true, 'solo');
        break;
      case 'guitar':
        guitar(mx, d, t, e.midi, dur, v, true);
        break;
      case 'guitarMute':
        guitarMute(mx, d, t, e.midi, dur, v, true);
        break;
      case 'piano':
        piano(mx, d, t, e.midi, dur, v, true);
        break;
      case 'strings':
        strings(mx, d, t, e.midi, dur, v, true);
        break;
      case 'brass':
        hornStab(mx, d, t, [e.midi], dur, v, true);
        break;
      case 'vibes':
        vibes(mx, d, t, e.midi, dur, v, true);
        break;
      case 'melodica':
        melodica(mx, d, t, e.midi, dur * 0.92, v, true);
        break;
      case 'flute':
        flute(mx, d, t, e.midi, dur * 0.95, v, true);
        break;
      case 'bass':
        switch (song.bassVoice) {
          case 'finger':
            fingerBass(mx, d, t, e.midi, dur * 0.9, v, true);
            break;
          case 'pick':
            pickBass(mx, d, t, e.midi, dur * 0.85, v, true);
            break;
          case 'upright':
            uprightBass(mx, d, t, e.midi, dur, v, true);
            break;
          default:
            softBass(mx, d, t, e.midi, dur, v, true);
        }
        break;
      case 'kick':
        drum(mx, d, t, song.feel === 'ballad' || song.style === 'easy' ? 'kickSoft' : 'kick', v, true);
        break;
      default:
        drum(mx, d, t, e.inst as DrumKind, v, true, dur);
    }
  }

  // ---- talk -----------------------------------------------------------------------------------

  private scheduleTalk(item: Item, from: number, to: number, origin: number): void {
    const talk = item.talk;
    if (!talk) return;
    const m = this.musicIn;
    if (m && talk.speechEnd > talk.speechStart && !this.ducked.has(item.index) && item.start + talk.speechStart < to + 0.3) {
      this.ducked.add(item.index);
      const s = origin + item.start + talk.speechStart;
      const e = origin + item.start + talk.speechEnd;
      m.gain.setTargetAtTime(MUSIC_LEVEL * DJ_DUCK, Math.max(this.mx.now(), s - 0.15), 0.08);
      m.gain.setTargetAtTime(MUSIC_LEVEL, e + 0.1, 0.3);
    }
    for (const c of talk.cues) {
      const at = item.start + c.t;
      if (at < from || at >= to) continue;
      const t = origin + at;
      if (c.kind === 'syl') this.scheduleSyllable(c, t);
      else if (c.kind === 'note') this.scheduleNote(c, t);
      else this.scheduleSfx(c, t);
    }
  }

  private scheduleNote(n: NoteCue, t: number): void {
    const mx = this.mx;
    const d = this.sum as AudioNode;
    switch (n.inst) {
      case 'vibes':
        vibes(mx, d, t, n.midi, n.dur, n.vel, true);
        break;
      case 'brass':
        hornStab(mx, d, t, [n.midi], n.dur, n.vel * 1.3, true);
        break;
      case 'bell':
        bellPartials(mx, d, t, 440 * Math.pow(2, (n.midi - 69) / 12), n.dur, 0.15 * n.vel, [1, 2.76, 5.4], true);
        break;
      case 'hammond':
        hammond(mx, d, t, n.midi, n.dur, n.vel, true, 'solo');
        break;
      case 'strings':
        strings(mx, d, t, n.midi, n.dur, n.vel, true);
        break;
      case 'flute':
        flute(mx, d, t, n.midi, n.dur, n.vel, true);
        break;
      case 'melodica':
        melodica(mx, d, t, n.midi, n.dur, n.vel * 1.2, true);
        break;
      case 'piano':
        piano(mx, d, t, n.midi, n.dur, n.vel, true);
        break;
      case 'guitar':
        guitar(mx, d, t, n.midi, n.dur, n.vel, true);
        break;
      default:
        epiano(mx, d, t, n.midi, n.dur, n.vel, true);
    }
  }

  private scheduleSfx(c: SfxCue, t: number): void {
    const mx = this.mx;
    const d = this.sum as AudioNode;
    const g = c.level;
    switch (c.name) {
      case 'ring':
        for (const off of [0, 1.0]) {
          tone(mx, { type: 'sine', f0: 440, t: t + off, dur: 0.4, gain: 0.08 * g, attack: 0.01, release: 0.05, dest: d, essential: true });
          tone(mx, { type: 'sine', f0: 480, t: t + off, dur: 0.4, gain: 0.08 * g, attack: 0.01, release: 0.05, dest: d, essential: true });
        }
        break;
      case 'pip':
        tone(mx, { type: 'sine', f0: 1000, t, dur: 0.1, gain: 0.3 * g, attack: 0.003, release: 0.01, dest: d, essential: true });
        break;
      case 'pipLong':
        tone(mx, { type: 'sine', f0: 1000, t, dur: 0.5, gain: 0.3 * g, attack: 0.003, release: 0.01, dest: d, essential: true });
        break;
      case 'teleprinter': {
        // A clatter of type bars: 11 Hz irregular ticks, fading.
        const n = Math.floor(c.dur * 11);
        for (let i = 0; i < n; i++) {
          const at = t + i / 11 + ((i * 7919) % 13) * 0.004;
          const fade = 1 - i / n;
          ringTick(mx, d, at, 1800 + ((i * 2654435) % 700), 3, 0.05 * g * fade, 0.018, true);
          if (i % 9 === 8) bellPartials(mx, d, at, 2600, 0.25, 0.03 * g, [1, 2.4], true);
        }
        break;
      }
      case 'bell':
        bellPartials(mx, d, t, 1046, 0.7, 0.14 * g, [1, 2.76, 5.4], true);
        bellPartials(mx, d, t + 0.4, 1318, 0.8, 0.12 * g, [1, 2.76, 5.4], true);
        break;
      case 'register':
        bellPartials(mx, d, t, 2093, 0.45, 0.14 * g, [1, 2.01], true);
        bellPartials(mx, d, t + 0.08, 2637, 0.6, 0.14 * g, [1, 2.01], true);
        noiseBurst(mx, { kind: 'brown', t: t + 0.25, dur: 0.12, gain: 0.25 * g, filter: 'lowpass', f0: 500, dest: d, essential: true });
        break;
      case 'horn':
        tone(mx, { type: 'sawtooth', f0: 392, t, dur: 0.45, gain: 0.08 * g, attack: 0.02, release: 0.06, lp: 2200, dest: d, essential: true });
        tone(mx, { type: 'sawtooth', f0: 494, t, dur: 0.45, gain: 0.07 * g, attack: 0.02, release: 0.06, lp: 2200, dest: d, essential: true });
        break;
      case 'chime':
        bellPartials(mx, d, t, 659, 0.9, 0.14 * g, [1, 2.76, 5.4], true);
        bellPartials(mx, d, t + 0.45, 523, 1.1, 0.14 * g, [1, 2.76, 5.4], true);
        break;
      case 'static':
        noiseBurst(mx, { kind: 'white', t, dur: Math.max(0.1, c.dur), gain: 0.1 * g, filter: 'bandpass', f0: 2500, q: 0.7, dest: d, essential: true });
        break;
      case 'kickDrum':
        drum(mx, d, t, 'kick', g, true);
        drum(mx, d, t, 'crash', g * 0.5, true, 0.8);
        break;
      case 'applause':
        noiseBurst(mx, { kind: 'pink', t, dur: Math.max(0.5, c.dur), gain: 0.14 * g, attack: 0.15, release: c.dur * 0.4, filter: 'bandpass', f0: 1800, q: 0.6, dest: d, essential: true });
        for (let i = 0; i < 10; i++) ringTick(mx, d, t + 0.1 + i * (c.dur / 11), 1400 + ((i * 977) % 900), 2, 0.04 * g, 0.03, true);
        break;
    }
  }

  private makeVoice(phone: boolean): FormantVoice {
    const mx = this.mx;
    const ctx = mx.ctx as AudioContext;
    const nodes: AudioNode[] = [];
    const reg = <T extends AudioNode>(n: T): T => {
      nodes.push(n);
      return n;
    };
    const osc = reg(ctx.createOscillator());
    osc.type = 'sawtooth';
    osc.frequency.value = 120;
    // The glottal source is a saw; a narrow formant bandpass keeps only a harmonic or two of it, so the
    // source runs hot (the formant gains below bring the voice up to the level of a record).
    const src = reg(ctx.createGain());
    src.gain.value = 3.2;
    const env = reg(ctx.createGain());
    env.gain.value = 0.0001;
    const bands: BiquadFilterNode[] = [];
    for (const [f, q, g] of [
      [500, 5, 1],
      [1500, 7, 0.55],
      [2500, 8, 0.25],
    ] as const) {
      const bp = reg(ctx.createBiquadFilter());
      bp.type = 'bandpass';
      bp.Q.value = q;
      bp.frequency.value = f;
      const bg = reg(ctx.createGain());
      bg.gain.value = g;
      src.connect(bp).connect(bg).connect(env);
      bands.push(bp);
    }
    osc.connect(src);
    let out: AudioNode = env;
    if (phone) {
      // Down the line: narrow band, a bit of crunch.
      const hp = reg(ctx.createBiquadFilter());
      hp.type = 'highpass';
      hp.frequency.value = 380;
      const lp = reg(ctx.createBiquadFilter());
      lp.type = 'lowpass';
      lp.frequency.value = 2600;
      lp.Q.value = 1.5;
      const sat = reg(ctx.createWaveShaper());
      sat.curve = softClipCurve(2.2);
      const g = reg(ctx.createGain());
      g.gain.value = 1.0;
      env.connect(hp).connect(lp).connect(sat).connect(g);
      out = g;
    }
    out.connect(this.voiceIn as AudioNode);
    osc.start();
    return { osc, bp: [bands[0] as BiquadFilterNode, bands[1] as BiquadFilterNode, bands[2] as BiquadFilterNode], env, nodes };
  }

  private dropVoices(): void {
    for (const v of [this.studio, this.phone]) {
      if (!v) continue;
      try {
        v.osc.stop();
      } catch {
        /* already stopped */
      }
      for (const n of v.nodes) n.disconnect();
    }
    this.studio = null;
    this.phone = null;
  }

  private scheduleSyllable(s: Syllable, t: number): void {
    if (!this.voiceIn) return;
    const phone = s.speaker === 'caller' || s.speaker === 'caller2';
    const v = phone ? (this.phone ??= this.makeVoice(true)) : (this.studio ??= this.makeVoice(false));
    const e = t + s.dur;
    v.osc.frequency.setValueAtTime(s.f0, t);
    v.osc.frequency.linearRampToValueAtTime(s.f0End, e);
    v.bp[0].frequency.setValueAtTime(s.f1, t);
    v.bp[0].frequency.linearRampToValueAtTime(s.f1End, e);
    v.bp[1].frequency.setValueAtTime(s.f2, t);
    v.bp[1].frequency.linearRampToValueAtTime(s.f2End, e);
    v.bp[2].frequency.setValueAtTime(s.f3, t);
    const onset = s.plosive > 0 ? 0.012 : 0.03;
    v.env.gain.setValueAtTime(0.0001, t);
    v.env.gain.linearRampToValueAtTime(0.55 * s.amp, t + Math.min(onset, s.dur * 0.3));
    v.env.gain.linearRampToValueAtTime(0.36 * s.amp, t + s.dur * 0.7);
    v.env.gain.linearRampToValueAtTime(0.0001, e);
    const d = this.voiceIn;
    if (s.fric > 0) noiseBurst(this.mx, { kind: 'white', t: t - 0.02, dur: Math.min(0.08, s.dur * 0.6), gain: 0.07 * s.fric, filter: 'highpass', f0: phone ? 2200 : 3600, dest: d, essential: true });
    if (s.plosive > 0) noiseBurst(this.mx, { kind: 'white', t, dur: 0.012, gain: 0.08 * s.plosive, filter: 'bandpass', f0: phone ? 1200 : 1600, q: 1.2, dest: d, essential: true });
  }

  // ---- tuning blip ------------------------------------------------------------------------------

  /** Two-note tuning sweep + static (or a dull click when switching off). */
  private blip(index: number): void {
    const mx = this.mx;
    if (!mx.ctx || !mx.running) return;
    const d = mx.buses.ui;
    const t = mx.now() + 0.005;
    if (index < 0) {
      tone(mx, { type: 'sine', f0: 600, f1: 260, sweepTime: 0.08, t, dur: 0.1, gain: 0.2, dest: d });
      return;
    }
    noiseBurst(mx, { kind: 'white', t, dur: 0.24, gain: 0.2, attack: 0.01, filter: 'bandpass', f0: 500, f1: 3600, q: 1.5, dest: d });
    tone(mx, { type: 'sine', f0: 740 * (1 + index * 0.06), t: t + 0.04, dur: 0.09, gain: 0.18, dest: d });
    tone(mx, { type: 'sine', f0: 988 * (1 + index * 0.06), t: t + 0.12, dur: 0.12, gain: 0.18, dest: d });
  }

  dispose(): void {
    this.stopSequencer();
    for (const t of this.timeouts) clearTimeout(t);
    this.timeouts.clear();
    this.dropVoices();
    if (this.chain) {
      for (const n of this.chain.nodes) {
        try {
          if (n instanceof AudioScheduledSourceNode) n.stop();
        } catch {
          /* not started */
        }
        n.disconnect();
      }
    }
    this.sum?.disconnect();
    this.musicIn?.disconnect();
    this.voiceIn?.disconnect();
    this.musicIn = null;
    this.voiceIn = null;
    this.chain = null;
    this.sum = null;
  }
}
